import { endpoints } from '@/services/api/endpoints';
import { http } from '@/services/api/http';
import type {
  AddMediaToCartPayload,
  BookmarksResponse,
  BookOrderDetailResponse,
  BookOrdersResponse,
  BookReceiptResponse,
  CreateBookmarkPayload,
  CreateBookmarkResponse,
  DeliveryQuotePayload,
  DeliveryQuoteResponse,
  LibraryResponse,
  MediaBookCheckoutVerifyResponse,
  MediaBookDetailResponse,
  MediaCartResponse,
  MediaCartSummaryResponse,
  MediaCheckoutPayload,
  MediaCheckoutResponse,
  MediaResponse,
  ReadingProgressResponse,
  ReaderContentResponse,
  ReaderSessionResponse,
  SaveReadingProgressPayload,
} from '@/types/media';

export interface GetBooksParams {
  type?: string;
  search?: string;
  page?: number;
  limit?: number;
  currPage?: number;
  perPage?: number;
  conventionId?: string | null;
}

export const mediaService = {
  getBooks: async ({
    type,
    search = '',
    page = 1,
    limit = 25,
    currPage = 1,
    perPage = 25,
    conventionId,
  }: GetBooksParams = {}) => {
    const response = await http.get<MediaResponse>(
      endpoints.publicBooks({ type, search, page, limit, currPage, perPage, conventionId }),
    );

    if (!response.status) {
      throw new Error('Unable to load books.');
    }

    return response;
  },

  getBookDetail: async (bookId: string) => {
    const response = await http.get<MediaBookDetailResponse>(
      endpoints.publicBookDetail(bookId),
    );

    if (!response.status) {
      throw new Error('Unable to load book details.');
    }

    return response;
  },

  addToCart: async (payload: AddMediaToCartPayload) => {
    const response = await http.post<MediaCartResponse, AddMediaToCartPayload>(
      endpoints.mediaCartItems,
      payload,
    );

    if (!response.status) {
      throw new Error('Unable to add item to cart.');
    }

    return response;
  },

  getCart: async () => {
    const response = await http.get<MediaCartSummaryResponse>(endpoints.mediaCart);

    if (!response.status) {
      throw new Error('Unable to load cart.');
    }

    return response;
  },

  removeCartItem: async (cartItemId: string) => {
    const response = await http.delete<{ status: boolean; message?: string }>(
      endpoints.mediaCartItem(cartItemId),
    );

    if (!response.status) {
      throw new Error('Unable to remove item from cart.');
    }

    return response;
  },

  updateCartItemQuantity: async (cartItemId: string, quantity: number) => {
    const response = await http.patch<MediaCartSummaryResponse, { quantity: number }>(
      endpoints.mediaCartItem(cartItemId),
      { quantity },
    );

    if (!response.status) {
      throw new Error('Unable to update cart item.');
    }

    return response;
  },

  deliveryQuote: async (payload: DeliveryQuotePayload) => {
    const response = await http.post<DeliveryQuoteResponse, DeliveryQuotePayload>(
      endpoints.mediaDeliveryQuote,
      payload,
    );

    if (!response.status) {
      throw new Error('Unable to fetch delivery quote.');
    }

    return response;
  },

  checkout: async (payload: MediaCheckoutPayload) => {
    const response = await http.post<MediaCheckoutResponse, MediaCheckoutPayload>(
      endpoints.mediaCheckout,
      payload,
    );

    if (!response.status) {
      throw new Error('Unable to process checkout.');
    }

    return response;
  },

  verifyBookCheckout: async (reference: string) => {
    const response = await http.post<MediaBookCheckoutVerifyResponse>(
      endpoints.mediaBookCheckoutVerify(reference),
    );

    if (!response.status) {
      throw new Error('Unable to verify checkout.');
    }

    return response;
  },

  getOrders: async ({ page = 1, limit = 25, status }: { page?: number; limit?: number; status?: string } = {}) => {
    const response = await http.get<BookOrdersResponse>(endpoints.mediaOrders({ page, limit, status }));

    if (!response.status) {
      throw new Error('Unable to load orders.');
    }

    return response;
  },

  getOrderDetail: async (orderId: string) => {
    const response = await http.get<BookOrderDetailResponse>(endpoints.mediaOrderDetail(orderId));

    if (!response.status) {
      throw new Error('Unable to load order details.');
    }

    return response;
  },

  getBookReceipt: async (reference: string) => {
    const response = await http.get<BookReceiptResponse>(
      endpoints.mediaBookReceipt(reference),
    );

    if (!response.status) {
      throw new Error('Unable to load receipt.');
    }

    return response;
  },

  getLibrary: async ({ page = 1, limit = 25 }: { page?: number; limit?: number } = {}) => {
    const response = await http.get<LibraryResponse>(endpoints.mediaLibrary({ page, limit }));

    if (!response.status) {
      throw new Error('Unable to load library.');
    }

    return response;
  },

  /* ---------- Reader ---------- */

  createReaderSession: async (entitlementId: string) => {
    const response = await http.post<ReaderSessionResponse, { entitlementId: string }>(
      endpoints.readerSession,
      { entitlementId },
    );

    if (!response.status) {
      throw new Error('Unable to create reader session.');
    }

    return response;
  },

  getReaderContent: async (contentEndpoint: string) => {
    const response = await http.get<ReaderContentResponse>(contentEndpoint);

    if (!response.status) {
      throw new Error('Unable to fetch book content.');
    }

    return response;
  },

  getReadingProgress: async () => {
    const response = await http.get<ReadingProgressResponse>(endpoints.readerProgress);

    if (!response.status) {
      throw new Error('Unable to load reading progress.');
    }

    return response;
  },

  saveReadingProgress: async (payload: SaveReadingProgressPayload) => {
    const response = await http.post<ReadingProgressResponse, SaveReadingProgressPayload>(
      endpoints.readerProgress,
      payload,
    );

    if (!response.status) {
      throw new Error('Unable to save reading progress.');
    }

    return response;
  },

  createBookmark: async (payload: CreateBookmarkPayload) => {
    const response = await http.post<CreateBookmarkResponse, CreateBookmarkPayload>(
      endpoints.readerBookmarks,
      payload,
    );

    if (!response.status) {
      throw new Error('Unable to create bookmark.');
    }

    return response;
  },

  getBookmarks: async (entitlementId: string) => {
    const params = new URLSearchParams();
    params.set('entitlementId', entitlementId);
    const response = await http.get<BookmarksResponse>(
      `${endpoints.readerBookmarks}?${params.toString()}`,
    );

    if (!response.status) {
      throw new Error('Unable to load bookmarks.');
    }

    return response;
  },
};
